import React from 'react'
import { Link } from 'react-router-dom'
import Icon from '../components/Icons'
import Reveal, { MaskLines } from '../components/Reveal'
import { CountUp, Marquee } from '../components/Motion'
import SectionIntro from '../components/SectionIntro'
import { LogoMark } from '../components/Logo'
import ProductCards from '../components/ProductCards'
import {
  company,
  services,
  differentiators,
  stats,
  values,
  vision,
  mission,
  sectors,
  partners,
  clients,
} from '../data/site'

const Home = () => (
  <main>
    <Hero />
    <CapabilityTicker />
    <Manifesto />
    <ServiceIndex />
    <StatsBand />
    <Sectors />
    <Work />
    <Network />
    <Approach />
    <Directive />
    <ValuesTicker />
    <CallToAction />
  </main>
)

/* ---------------------------------------------------------------- Hero */
const Hero = () => (
  <section className="relative flex min-h-[94vh] flex-col justify-between overflow-hidden bg-ink-950 grain">
    <div
      className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/hero-tech-office.png')" }}
      aria-hidden="true"
    />
    <div className="pointer-events-none absolute inset-0 bg-ink-950/25" aria-hidden="true" />

    {/* Deliberately grid-free: the hero should feel calm and considered, not technical for its own sake. */}
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-paper-100/25 to-transparent"
      aria-hidden="true"
    />
    <div
      className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full border border-paper-100/[0.07]"
      aria-hidden="true"
    />

    <div className="container relative flex flex-1 items-center pb-20 pt-40 md:pb-24 md:pt-48">
      <div className="w-full lg:max-w-5xl">
        <Reveal>
          <p className="label label-dark flex items-center gap-3">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-400 shadow-[0_0_0_5px_rgba(143,174,255,0.08)]" />
            {company.location}
            <span className="h-px w-7 bg-paper-100/25" aria-hidden="true" />
            ICT &amp; Creative Solutions
          </p>
        </Reveal>

        <MaskLines
          as="h1"
          className="display-1 mt-9 text-paper-100"
          lines={[
            'Technology',
            'built to move your',
            <>
              business <span className="accent-italic">forward</span>
            </>,
          ]}
        />

        <div className="mt-14 grid gap-10 border-t border-paper-100/15 pt-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-end md:pt-9">
          <Reveal delay={380}>
            <p className="max-w-xl text-lg leading-relaxed text-paper-100/60">
              {company.summary}
            </p>
          </Reveal>

          <Reveal delay={460} className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn btn-light">
              Start a project
              <Icon name="arrow" className="h-4 w-4" />
            </Link>
            <Link to="/services" className="btn btn-ghost-dark">
              What we do
            </Link>
          </Reveal>
        </div>
      </div>
    </div>

    {/* Bottom rail: scroll cue + credentials */}
    <div className="container relative pb-8">
      <div className="rule rule-dark" />
      <div className="flex flex-wrap items-center justify-between gap-6 pt-6">
        <div className="flex items-center gap-4">
          <span className="relative hidden h-10 w-px overflow-hidden bg-paper-100/15 sm:block">
            <span className="absolute inset-x-0 top-0 h-1/2 animate-scroll-cue bg-accent-400" />
          </span>
          <span className="label label-dark">Scroll</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
          {['Kenyan registered', 'Tender & procurement ready', 'Genuine licensing'].map((item) => (
            <span key={item} className="label label-dark">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
)

/* ------------------------------------------------------ Capability ticker */
const CapabilityTicker = () => (
  <section className="border-y border-ink-950/10 bg-paper-100 py-7">
    <Marquee>
      {services.map((service) => (
        <span key={service.slug} className="flex items-center">
          <span className="whitespace-nowrap px-9 font-display text-2xl font-medium tracking-tighter2 text-ink-950 md:text-3xl">
            {service.short}
          </span>
          <LogoMark className="h-4 w-4 shrink-0 text-accent-500" />
        </span>
      ))}
    </Marquee>
  </section>
)

/* ------------------------------------------------------------ Manifesto */
const Manifesto = () => (
  <section className="section">
    <div className="container">
      <div className="grid gap-10 md:grid-cols-[10rem_1fr] md:gap-12">
        <Reveal className="flex items-baseline gap-4 pt-3">
          <span className="index-num">01</span>
          <span className="label">Who we are</span>
        </Reveal>

        <div>
          <MaskLines
            as="h2"
            className="display-2 max-w-4xl"
            lines={[
              'A Kenyan partner for',
              <>
                technology, supply and <span className="accent-italic">craft</span>
              </>,
            ]}
          />

          <div className="mt-14 grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal delay={200} className="space-y-6 text-lg leading-relaxed text-ink-500">
              {company.about.map((para) => (
                <p key={para.slice(0, 24)}>{para}</p>
              ))}
              <Link to="/about" className="link-arrow group pt-2">
                <span className="border-b border-ink-950/30 pb-1 transition-colors group-hover:border-ink-950">
                  More about us
                </span>
                <Icon
                  name="arrow"
                  className="h-4 w-4 transition-transform duration-500 ease-out-expo group-hover:translate-x-1"
                />
              </Link>
            </Reveal>

            <Reveal delay={320}>
              <dl className="divide-y divide-ink-950/10 border-y border-ink-950/10">
                {[
                  ['Registration', 'Kenyan-registered'],
                  ['Head office', company.location],
                  ['Clients', 'Business, institutions, government'],
                  ['Routes', 'Direct supply & competitive tender'],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-6 py-5">
                    <dt className="label">{k}</dt>
                    <dd className="text-right font-display text-base font-medium text-ink-950">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
)

/* -------------------------------------------------------- Service index */
/* Numbered full-width rows that invert on hover — reads like a studio index
   rather than a grid of identical cards. */
export const ServiceRow = ({ service, index, to = '/services' }) => (
  <Link
    to={to}
    className="group relative block overflow-hidden border-t border-ink-950/10 last:border-b"
  >
    <span
      className="absolute inset-0 origin-bottom scale-y-0 bg-ink-950 transition-transform duration-500 ease-out-expo group-hover:scale-y-100"
      aria-hidden="true"
    />
    <div className="relative grid items-baseline gap-x-10 gap-y-4 px-1 py-9 md:grid-cols-[4rem_1fr_22rem_3rem] md:py-11">
      <span className="index-num transition-colors duration-500 group-hover:text-paper-100/50">
        {String(index + 1).padStart(2, '0')}
      </span>

      <h3 className="display-3 transition-all duration-500 ease-out-expo group-hover:text-paper-100 md:group-hover:translate-x-2">
        {service.title}
      </h3>

      <p className="max-w-md text-ink-500 transition-colors duration-500 group-hover:text-paper-100/60">
        {service.blurb}
      </p>

      <span className="flex h-11 w-11 items-center justify-center justify-self-start rounded-full ring-1 ring-inset ring-ink-950/15 transition-all duration-500 ease-out-expo group-hover:bg-accent-500 group-hover:ring-accent-500 md:justify-self-end">
        <Icon
          name="arrow"
          className="h-4 w-4 text-ink-950 transition-all duration-500 ease-out-expo group-hover:translate-x-0.5 group-hover:text-paper-100"
        />
      </span>
    </div>
  </Link>
)

const ServiceIndex = () => (
  <section className="section bg-paper-50">
    <div className="container">
      <SectionIntro
        index="02"
        label="What we do"
        lines={['Eight practice areas,', <>one accountable <span className="accent-italic">partner</span></>]}
        description="Technology, supply, and creative work delivered directly or through competitive tender."
      />

      <div className="mt-20">
        {services.map((service, i) => (
          <Reveal key={service.slug} delay={i * 60}>
            <ServiceRow service={service} index={i} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
)

/* ----------------------------------------------------------- Stats band */
const StatsBand = () => (
  <section className="relative overflow-hidden bg-ink-950 bg-glow grain">
    <div className="container relative py-24 md:py-28">
      <dl className="grid gap-x-10 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <Reveal key={stat.label} delay={i * 90}>
            <div className="border-t border-paper-100/15 pt-6">
              <dt className="font-display text-6xl font-semibold tracking-tightest text-paper-100 md:text-7xl">
                <CountUp value={stat.value} />
              </dt>
              <dd className="label label-dark mt-4">{stat.label}</dd>
            </div>
          </Reveal>
        ))}
      </dl>
    </div>
  </section>
)

/* ------------------------------------------------------------ Sectors */
const Sectors = () => (
  <section className="border-b border-ink-950/10 bg-paper-50 py-16 md:py-20">
    <div className="container grid gap-10 md:grid-cols-[0.8fr_1.2fr] md:items-end">
      <Reveal>
        <span className="label">Sectors we support</span>
        <h2 className="mt-5 font-display text-3xl font-medium tracking-tighter2 text-ink-950 md:text-4xl">
          Built around the work that keeps organisations moving.
        </h2>
      </Reveal>
      <Reveal delay={120} className="flex flex-wrap gap-3 md:justify-end">
        {sectors.map((sector) => (
          <span key={sector} className="rounded-full border border-ink-950/15 px-4 py-2 text-sm text-ink-500">
            {sector}
          </span>
        ))}
      </Reveal>
    </div>
  </section>
)

/* ------------------------------------------------------ Products & work */
const Work = () => (
  <section className="section bg-paper-100">
    <div className="container">
      <SectionIntro
        index="03"
        label="Products & projects"
        lines={['Technology made', <>useful in the <span className="accent-italic">real world</span></>]}
        description="A selection of products and digital work currently available through Aldaron Limited."
      />
      <div className="mt-20"><ProductCards /></div>
      <Reveal delay={160} className="mt-10">
        <Link to="/products" className="link-arrow group">
          <span className="border-b border-ink-950/30 pb-1 transition-colors group-hover:border-ink-950">Explore all products</span>
          <Icon name="arrow" className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
)

/* -------------------------------------------------- Partners & clients */
const LogoRail = ({ label, items, reverse = false }) => (
  <div className="mt-7">
    <div className="logo-rail relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-paper-50 to-transparent sm:w-20" aria-hidden="true" />
      <Marquee reverse={reverse} scrollable className="logo-marquee">
        {items.map((item) => (
          <div
            key={item.name}
            role="listitem"
            className="group mx-2 flex h-32 w-[min(76vw,21rem)] shrink-0 items-center justify-center px-8 transition-transform duration-500 ease-out-expo hover:-translate-y-1 sm:mx-3 sm:h-36 sm:w-72"
          >
            <img
              src={item.image}
              alt={item.name}
              className="max-h-full w-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
          </div>
        ))}
      </Marquee>
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-paper-50 to-transparent sm:w-20" aria-hidden="true" />
    </div>
    <p className="mt-4 label">{label}</p>
  </div>
)

const Network = () => (
  <section className="section bg-paper-50">
    <div className="container">
      <SectionIntro
        index="04"
        label="Our network"
        lines={['Trusted by teams', <>doing important <span className="accent-italic">work</span></>]}
        description="We are proud to work alongside organisations across Kenya."
      />
      <div className="mt-20 border-y border-ink-950/10 py-8">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <p className="max-w-sm text-lg leading-relaxed text-ink-500">
            A growing network of organisations trusting us with the work that keeps them moving.
          </p>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-4xl font-medium tracking-tighter2 text-ink-950">110+</span>
            <span className="label">clients served</span>
          </div>
        </div>
        <LogoRail label="Partner" items={partners} />
        <LogoRail label="Clients" items={clients} reverse />
      </div>
    </div>
  </section>
)

/* ------------------------------------------------------------- Approach */
const Approach = () => (
  <section className="section">
    <div className="container">
      <div className="grid gap-16 lg:grid-cols-[0.85fr_1.15fr] lg:gap-24">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <Reveal className="flex items-baseline gap-4">
            <span className="index-num">05</span>
            <span className="label">Why Aldaron</span>
          </Reveal>
          <MaskLines
            as="h2"
            className="display-2 mt-8"
            lines={['Depth and range', <>from one <span className="accent-italic">team</span></>]}
          />
          <Reveal delay={240}>
            <p className="mt-8 max-w-sm text-lg leading-relaxed text-ink-500">
              Most clients need more than one discipline. We remove the seams between them.
            </p>
            <Link to="/contact" className="btn btn-outline mt-10">
              Start a conversation
              <Icon name="arrow" className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div>
          {differentiators.map((item, i) => (
            <Reveal key={item.title} delay={i * 70}>
              <article className="group grid gap-5 border-t border-ink-950/10 py-9 md:grid-cols-[3rem_1fr] md:gap-8 md:py-10">
                <span className="index-num pt-1">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <div className="flex items-start gap-4">
                    <Icon
                      name={item.icon}
                      className="mt-0.5 h-5 w-5 shrink-0 text-accent-500"
                    />
                    <h3 className="font-display text-xl font-medium tracking-tighter2 text-ink-950">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-4 max-w-lg text-ink-500 md:pl-9">{item.blurb}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
)

/* ------------------------------------------------------ Vision & mission */
const Directive = () => (
  <section className="section bg-paper-50">
    <div className="container">
      <SectionIntro
        index="06"
        label="Direction"
        lines={['Where we are going,', <>and how we <span className="accent-italic">get there</span></>]}
      />

      <div className="mt-20 grid gap-px overflow-hidden rounded-sm bg-ink-950/10 md:grid-cols-2">
        {[
          { title: 'Vision', body: vision },
          { title: 'Mission', body: mission },
        ].map((block, i) => (
          <Reveal key={block.title} delay={i * 120}>
            <div className="h-full bg-paper-50 p-9 md:p-12">
              <span className="label">{block.title}</span>
              <p className="mt-8 font-display text-2xl font-normal leading-snug tracking-tighter2 text-ink-950 md:text-[1.75rem]">
                {block.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
)

/* ---------------------------------------------------------- Values rail */
const ValuesTicker = () => (
  <section className="border-y border-ink-950/10 bg-paper-100 py-10">
    <Marquee speed="fast">
      {values.map((value) => (
        <span key={value} className="flex items-center">
          <span className="whitespace-nowrap px-8 font-display text-xl font-medium tracking-tighter2 text-ink-950/70">
            {value}
          </span>
          <span className="h-1 w-1 shrink-0 rounded-full bg-ochre" />
        </span>
      ))}
    </Marquee>
  </section>
)

/* ------------------------------------------------------------------ CTA */
export const CallToAction = () => (
  <section className="relative overflow-hidden bg-ink-950 bg-glow grain">
    <div className="container relative py-28 md:py-36">
      <MaskLines
        as="h2"
        className="display-1 max-w-5xl text-paper-100"
        lines={[
          'Let’s talk about',
          <>
            your <span className="accent-italic">next</span> project
          </>,
        ]}
      />

      <div className="mt-16 grid gap-10 md:grid-cols-[1fr_auto] md:items-end">
        <Reveal delay={260}>
          <p className="max-w-md text-lg leading-relaxed text-paper-100/55">
            Send us the brief, the tender documents, or just a question — we will come back
            with a clear next step.
          </p>
        </Reveal>

        <Reveal delay={340} className="flex flex-wrap gap-3">
          <Link to="/contact" className="btn btn-light">
            Contact us
            <Icon name="arrow" className="h-4 w-4" />
          </Link>
          <a
            href={`tel:${company.phones[0].replace(/\s/g, '')}`}
            className="btn btn-ghost-dark"
          >
            <Icon name="phone" className="h-4 w-4" />
            {company.phones[0]}
          </a>
        </Reveal>
      </div>
    </div>
  </section>
)

export default Home
